import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../../utils/api';
import { fmtDateTime } from '../../../../utils/region';
import type { AwCtx } from '../../ctx';
import { baht, card, dmy, errBox, note, num, openBlob, pillAmber, pillGreen, pillGrey, pillRed, sectionHead, table, td, th } from '../../ui';
import { Dialog, Loader, Stat, actBtn, emptyLine } from '../../dialog';
import type { FaAsset, FaDetail, FaStatus, FaCategory } from './shared';
import { STATUS_KEY } from './shared';
import { CloseBtn, DeleteDialog, DisposeDialog, Fact, FaAcct, FaPosted, WriteOffDialog, acctLabel, fmtNum, pickName } from './AssetActions';

// ── One asset, full screen (batch 9 R4 — COA-H4 / H6 / H7 / H16) ─────────────────────────────────
// Everything the register knows about one asset, read from GET /fixed-assets/assets/:id (services/
// fixedAssets.ts assetDetail) and laid out — nothing is computed here that the server did not send:
//   · the eighteen fields in the customer's order (Asset Mgt.docx "Required Information"), each
//     setting the asset holds AGAINST its category marked "set by hand" (asset.overrides, COA-H3),
//     and beside the method the company's charging frequency, read-only (decision Q6 — `cadence`);
//   · its three accounts and the figure brought forward from before this system, with the opening
//     net book value it leaves (total cost − that figure);
//   · ⛔ an asset that has LEFT (disposed of / written off) carries no book value: "—", and what it
//     carried when it went under its own name (asset.nbv_at_disposal);
//   · every depreciation charge with the entry it is in, and the disposal entry;
//   · its history (fixed_asset_movements): registered, every field changed with both values — a
//     move of location is one of them — disposed, written off, discarded, reversed, documents;
//   · its supporting documents, opened in place of a download, added and taken away by a writer —
//     a disposed asset's too (the sale invoice belongs to it; services/assetFiles.ts only asks it be live);
//   · where it was bought, as a jump to that Expense record (COA-H7).
// The actions (edit, dispose, write off, delete) are offered only to a writer and only when they
// apply; each refusal is the server's own sentence. Closing returns to the register as it was (FS-9):
// the window is ?fa=<id> over a list that never unmounted.
// ⛔ A REMOVED asset (soft-deleted, asset.removed) still opens — the house rule is that a deleted row
// stays, marked — read-only under a banner naming who removed it and when: no action, no upload, no
// remove, and its documents listed by name only (the server no longer serves a removed asset's files).

type TF = (key: string, opts?: Record<string, unknown>) => string;

const STATUS_TONE: Record<string, React.CSSProperties> = {
  in_use: pillGreen, not_yet_available: pillAmber, idle: pillGrey, fully_depreciated: pillGrey, disposed: pillRed, written_off: pillRed,
};
export const StatusPill = ({ status, t }: { status: string; t: TF }) => {
  const k = STATUS_KEY[status as FaStatus];
  return <span data-status={status} style={STATUS_TONE[status] || pillGrey}>{k ? t(k) : t('aw_faa_status_auto')}</span>;
};
const ENTRY_KEY: Record<string, string> = { draft: 'aw_faa_entry_draft', posted: 'aw_faa_entry_posted', missing: 'aw_faa_entry_missing' };
const entryPill = (s: string) => (s === 'posted' ? pillGreen : s === 'draft' ? pillAmber : s === 'missing' ? pillRed : pillGrey);
const EntryState = ({ status, t }: { status: string; t: TF }) => (status ? <span style={entryPill(status)}>{ENTRY_KEY[status] ? t(ENTRY_KEY[status]) : status}</span> : null);

/** Each kind of history line, each key written in full so a grep finds it. */
const KIND_KEY: Record<string, string> = {
  registered: 'aw_faa_mv_registered', edit: 'aw_faa_mv_edit', disposed: 'aw_faa_mv_disposed', written_off: 'aw_faa_mv_written_off',
  disposal_discarded: 'aw_faa_mv_disposal_discarded', reversed: 'aw_faa_mv_reversed', file_added: 'aw_faa_mv_file_added',
  file_removed: 'aw_faa_mv_file_removed', deleted: 'aw_faa_mv_deleted',
};
const KIND_ICON: Record<string, string> = {
  registered: 'ti ti-circle-plus', edit: 'ti ti-pencil', disposed: 'ti ti-arrow-bar-right', written_off: 'ti ti-trash-x',
  disposal_discarded: 'ti ti-arrow-back-up', reversed: 'ti ti-arrows-exchange', file_added: 'ti ti-paperclip', file_removed: 'ti ti-unlink', deleted: 'ti ti-trash',
};
/** The column an edit changed (services/fixedAssets.ts COLUMN_OF), by the label the form gives it. */
export const FIELD_KEY: Record<string, string> = {
  name_th: 'aw_faa_f_name_th', name_en: 'aw_faa_f_name_en', vendor_name: 'aw_faa_f_supplier', vendor_invoice_no: 'aw_faa_f_invoice_no',
  location: 'aw_faa_f_location', status: 'aw_faa_f_status', note: 'aw_faa_f_note', category_id: 'aw_faa_f_category', method: 'aw_faa_f_method',
  cost: 'aw_faa_f_cost', capitalised_cost: 'aw_faa_f_capitalised', salvage: 'aw_faa_f_residual', rate: 'aw_faa_f_rate',
  useful_life_years: 'aw_faa_f_life', acquired_date: 'aw_faa_f_purchase_date', available_date: 'aw_faa_f_available_date',
  depreciation_start: 'aw_faa_f_start_date', opening_accum: 'aw_faa_f_opening', opening_as_of: 'aw_faa_f_opening_as_of',
  asset_account_id: 'aw_faa_acct_asset', accum_account_id: 'aw_faa_acct_accum', expense_account_id: 'aw_faa_acct_expense',
};
export const METHOD_KEY: Record<string, string> = { straight_line: 'aw_faa_method_straight_line' };
const SATANG_COLS = new Set(['cost', 'capitalised_cost', 'salvage', 'opening_accum']);
const DATE_COLS = new Set(['acquired_date', 'available_date', 'depreciation_start', 'opening_as_of']);
const ACCOUNT_COLS = new Set(['asset_account_id', 'accum_account_id', 'expense_account_id']);
const fmtSize = (n: number) => (n >= 1048576 ? `${fmtNum(n / 1048576, 1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
/** ⛔ Exactly what services/assetFiles.ts ASSET_FILE_TYPES / ASSET_FILE_MAX take — the server checks the
 *  file's TYPE (File.type), so that is what is checked here before anything is sent: a refusal is a JSON
 *  400, and every one pages the admins. No image/* — a TIFF or GIF scan would only be refused. */
const DOC_TYPES: Record<string, string> = {
  'application/pdf': '.pdf', 'image/jpeg': '.jpg,.jpeg', 'image/png': '.png', 'image/webp': '.webp', 'image/heic': '.heic',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
};
const DOC_ACCEPT = [...Object.values(DOC_TYPES), ...Object.keys(DOC_TYPES)].join(',');
const DOC_MAX = 15 * 1024 * 1024;
/** What GET /assets/:id adds to the register's shape (services/fixedAssets.ts assetDetail / shapeAsset). */
type FaAssetX = FaAsset & { removed?: boolean; deleted_by_name?: string | null; deleted_at?: number | null; nbv_at_disposal?: number | null };

export function AssetWindow({ ctx, canWrite, assetId, onClose, onEdit, onChanged }: { ctx: AwCtx; canWrite: boolean; assetId: string; onClose: () => void; onEdit: (id: string) => void; onChanged: () => void }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [d, setD] = useState<FaDetail | null>(null);
  const [loadErr, setLoadErr] = useState('');
  const [tick, setTick] = useState(0);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [posted, setPosted] = useState<{ kind: 'dispose' | 'writeoff'; r: FaPosted } | null>(null);
  const [dlg, setDlg] = useState<'dispose' | 'writeoff' | 'delete' | null>(null);
  const [accts, setAccts] = useState<FaAcct[] | null>(null);
  const [cats, setCats] = useState<FaCategory[] | null>(null);
  const [srcDoc, setSrcDoc] = useState('');
  const fileRef = useRef<HTMLInputElement | null>(null);
  // Deleted from here: the window is closing and must not read the asset back. The workspace's bump
  // (ctx.reload) can render before the router's close does — react-router runs that as a transition.
  const closing = useRef(false);

  useEffect(() => {
    if (closing.current) return;
    let alive = true;
    setLoadErr('');
    api.faAsset(ctx.clientId, assetId)
      .then((x: FaDetail) => { if (alive) setD(x); })
      .catch((e: any) => {
        if (!alive) return;
        setD(null); setLoadErr(e?.status === 404 ? t('aw_faa_not_found') : `${t('aw_faa_load_failed')}: ${String(e?.message || e)}`);
      });
    return () => { alive = false; };
  }, [ctx.clientId, assetId, ctx.reload, tick]);   // eslint-disable-line react-hooks/exhaustive-deps

  // The history names accounts and categories by id; they are read only when a line needs them.
  const needAccts = !!d?.movements.some(m => m.kind === 'edit' && ACCOUNT_COLS.has(m.field));
  const needCats = !!d?.movements.some(m => m.kind === 'edit' && m.field === 'category_id');
  useEffect(() => {
    if (!needAccts || accts) return;
    let alive = true;
    api.jnlAccounts(ctx.clientId).then((x: any) => { if (alive) setAccts(x?.accounts || []); }).catch(() => { if (alive) setAccts([]); });
    return () => { alive = false; };
  }, [needAccts, accts, ctx.clientId]);
  useEffect(() => {
    if (!needCats || cats) return;
    let alive = true;
    api.faCategories(ctx.clientId, true).then((x: any) => { if (alive) setCats(x?.categories || []); }).catch(() => { if (alive) setCats([]); });
    return () => { alive = false; };
  }, [needCats, cats, ctx.clientId]);
  // Where it was bought: the record's own number reads better than its key. Best effort — the key is
  // still the link when the record cannot be read.
  const srcKey = d?.source?.key || '';
  useEffect(() => {
    if (!srcKey) { setSrcDoc(''); return; }
    let alive = true;
    api.faSourceCandidates(ctx.clientId, srcKey)
      .then((x: any) => { if (alive) setSrcDoc(String(x?.record?.expenseNo || x?.record?.supplierNo || '')); })
      .catch(() => { if (alive) setSrcDoc(''); });
    return () => { alive = false; };
  }, [srcKey, ctx.clientId]);

  const a: FaAssetX | null = d?.asset || null;
  const disposed = !!a?.disposed_date;
  // Removed (soft-deleted): read-only for everyone — no write of any kind is offered.
  const removed = !!a?.removed;
  const canAct = canWrite && !removed;
  const reread = () => { setTick(n => n + 1); ctx.bump(); onChanged(); };
  /** One request at a time; a WRITE re-reads the asset and tells the workspace (ctx.bump), a read does not. */
  const run = async (key: string, fn: () => Promise<unknown>, write = true) => {
    setBusy(key); setErr('');
    try { await fn(); if (write) reread(); } catch (e: any) { setErr(String(e?.message || e)); } finally { setBusy(''); }
  };
  const openFile = (id: string) => run(`open-${id}`, async () => { openBlob(await api.faFileUrl(ctx.clientId, id)); }, false);
  const upload = (file: File | null | undefined) => {
    if (!file) return;
    // Checked before sending, against the server's own list and limit: a file it would refuse is not sent.
    if (!DOC_TYPES[file.type] || !file.size || file.size > DOC_MAX) {
      setErr(t('aw_faa_doc_types'));
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    run('upload', () => api.faUploadFile(ctx.clientId, assetId, file)).then(() => { if (fileRef.current) fileRef.current.value = ''; });
  };
  const removeFile = (id: string, name: string) => {
    if (!window.confirm(t('aw_faa_doc_remove_confirm', { name }))) return;
    run(`rm-${id}`, () => api.faDeleteFile(ctx.clientId, id));
  };
  const afterPost = (kind: 'dispose' | 'writeoff') => (r: FaPosted) => { setDlg(null); setPosted({ kind, r }); reread(); };
  // Deleted: the window closes — it never reads back the asset it has just taken off the register.
  const afterDelete = () => { closing.current = true; setDlg(null); onClose(); ctx.bump(); onChanged(); };
  // The draft has to be posted where entries are posted; the jump clears this screen's own state.
  const toJournal = () => ctx.setNav('accounting', 'journal_entries', { fa: '', faedit: '', fasrc: '', fatab: '' });
  const toSource = () => { if (d?.source) ctx.setNav('expenses', 'expense_records', { exp: d.source.key, fa: '', fatab: '' }); };

  const fmtVal = (field: string, v: string): React.ReactNode => {
    if (v === '' || v === null || v === undefined) return '—';
    if (SATANG_COLS.has(field)) return baht(v);
    if (DATE_COLS.has(field)) return dmy(v);
    if (field === 'rate') return `${fmtNum(v)}%`;
    if (field === 'useful_life_years') return t('aw_faa_life_years', { n: fmtNum(v) });
    if (field === 'status') return STATUS_KEY[v as FaStatus] ? t(STATUS_KEY[v as FaStatus]) : v;
    if (field === 'method') return METHOD_KEY[v] ? t(METHOD_KEY[v]) : v;
    if (ACCOUNT_COLS.has(field)) {
      const x = (accts || []).find(y => y.id === v);
      if (x) return acctLabel(lang, x);
      const ref = [d?.accounts.asset, d?.accounts.accum, d?.accounts.expense].find(y => y && y.id === v);
      return ref ? acctLabel(lang, ref) : t('aw_faa_acct_unknown');
    }
    if (field === 'category_id') {
      const c = (cats || []).find(y => y.id === v);
      return c ? pickName(lang, c.name_th, c.name_en) : t('aw_faa_cat_unknown');
    }
    return v;
  };

  const title = a ? pickName(lang, a.name_th, a.name_en) : t('aw_faa_window_title');
  const ov = new Set(a?.overrides || []);
  const startShown = !a ? '' : a.depreciation_start ? dmy(a.depreciation_start)
    : a.available_date ? t('aw_faa_start_is_available', { date: dmy(a.available_date) }) : t('aw_faa_start_is_purchase', { date: dmy(a.acquired_date) });
  const depreciable = !!a && Number((a as any).depreciable ?? 1) !== 0 && a.charge_nature !== 'none';
  // Left the register: no book value (the server sends 0), and what it carried when it went, by name.
  const atDisposal = disposed && a?.nbv_at_disposal !== null && a?.nbv_at_disposal !== undefined
    ? t('aw_faa_nbv_at_disposal', { amount: baht(a.nbv_at_disposal) }) : '';
  const removedPill = <span data-status="removed" style={pillRed}>{t('aw_faa_mv_deleted')}</span>;

  const footer = (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      {a && canAct && !disposed && (
        <button type="button" data-act="edit" onClick={() => onEdit(a.id)} disabled={!!busy} style={actBtn}><i className="ti ti-pencil" aria-hidden /> {t('aw_faa_act_edit')}</button>
      )}
      {a && canAct && !disposed && (
        <button type="button" data-act="dispose" onClick={() => setDlg('dispose')} disabled={!!busy} style={actBtn}><i className="ti ti-arrow-bar-right" aria-hidden /> {t('aw_faa_act_dispose')}</button>
      )}
      {a && canAct && !disposed && (
        <button type="button" data-act="writeoff" onClick={() => setDlg('writeoff')} disabled={!!busy} style={actBtn}><i className="ti ti-trash-x" aria-hidden /> {t('aw_faa_act_writeoff')}</button>
      )}
      {a && canAct && !disposed && !a.charged && (
        <button type="button" data-act="delete" onClick={() => setDlg('delete')} disabled={!!busy} style={{ ...actBtn, color: '#B42318' }}><i className="ti ti-trash" aria-hidden /> {t('aw_faa_act_delete')}</button>
      )}
      <div style={{ flex: 1 }} />
      <CloseBtn onClose={onClose} t={t} />
    </div>
  );

  return (
    <Dialog fullscreen title={title} docNo={a?.asset_no} onClose={onClose} busy={!!busy} footer={footer}>
      <div data-fa-window={assetId} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {loadErr && <div style={errBox}>{loadErr}</div>}
        {a && removed && (
          <div data-removed style={{ ...card, padding: '10px 12px', fontSize: 12.5, color: '#B42318', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <i className="ti ti-trash" aria-hidden style={{ marginTop: 2 }} />
            <div>{t('aw_faa_removed_banner', { by: a.deleted_by_name || '—', when: fmtDateTime(a.deleted_at) || '—' })}</div>
          </div>
        )}
        {err && <div data-err style={errBox}>{err}</div>}
        {posted && (
          <div data-notice="draft" style={{ ...card, padding: '10px 12px', fontSize: 12.5, color: '#92400E', display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <i className="ti ti-file-alert" aria-hidden style={{ marginTop: 2 }} />
            <div style={{ flex: '1 1 300px' }}>
              <b>{t(posted.kind === 'dispose' ? 'aw_faa_disposed_msg' : 'aw_faa_writtenoff_msg')}</b>{' '}
              {t('aw_faa_draft_made', { no: posted.r.docNo })}
              {posted.r.gainLoss !== undefined && <div>{t(posted.r.gainLoss >= 0 ? 'aw_faa_gain' : 'aw_faa_loss', { amount: baht(Math.abs(posted.r.gainLoss)) })}</div>}
              {posted.r.loss !== undefined && <div>{t('aw_faa_loss', { amount: baht(Math.abs(posted.r.loss)) })}</div>}
            </div>
            <button type="button" onClick={toJournal} style={actBtn}><i className="ti ti-book-2" aria-hidden /> {t('aw_faa_open_journal')}</button>
          </div>
        )}
        {!d && !loadErr && <Loader />}
        {a && d && (
          <>
            <div style={{ display: 'flex', gap: 10, alignItems: 'stretch', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '0 0 auto' }}>{removed ? removedPill : <StatusPill status={a.status} t={t} />}</div>
              <Stat label={t('aw_faa_stat_cost')} value={baht(a.capitalised_total)} />
              <Stat label={t('aw_faa_stat_accum')} value={baht(a.accumulated)} />
              <Stat label={t('aw_faa_stat_nbv')} value={<span data-stat="nbv">{disposed ? '—' : baht(a.nbv)}</span>} sub={atDisposal || undefined} tone="var(--accent-text)" />
            </div>

            <div>
              <div style={sectionHead}>{t('aw_faa_sec_fields')}</div>
              <div data-fa-fields style={{ ...card, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '14px 18px' }}>
                <Fact t={t} k="asset_no" label={t('aw_faa_f_asset_no')}><b style={{ fontVariantNumeric: 'tabular-nums' }}>{a.asset_no}</b></Fact>
                <Fact t={t} k="name" label={t('aw_faa_f_name')}>
                  {a.name_th || '—'}{a.name_en ? <div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{a.name_en}</div> : null}
                </Fact>
                <Fact t={t} k="category" label={t('aw_faa_f_category')}>
                  {a.category_id ? pickName(lang, a.category_th, a.category_en) : <span style={{ color: '#92400E' }}>{t('aw_faa_no_category')}</span>}
                  <div style={note}>{pickName(lang, a.class_th, a.class_en)}{(a as any).max_rate != null ? ` · ${t('aw_faa_ceiling_short', { max: fmtNum((a as any).max_rate) })}` : ''}</div>
                </Fact>
                <Fact t={t} k="purchase_date" label={t('aw_faa_f_purchase_date')}>{dmy(a.acquired_date) || '—'}</Fact>
                <Fact t={t} k="supplier" label={t('aw_faa_f_supplier')}>{a.vendor_name || '—'}</Fact>
                <Fact t={t} k="invoice_no" label={t('aw_faa_f_invoice_no')}><span style={{ fontVariantNumeric: 'tabular-nums' }}>{a.vendor_invoice_no || '—'}</span></Fact>
                <Fact t={t} k="cost" label={t('aw_faa_f_cost')}><span style={{ fontVariantNumeric: 'tabular-nums' }}>{baht(a.cost)}</span></Fact>
                <Fact t={t} k="capitalised" label={t('aw_faa_f_capitalised')}>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>{baht(a.capitalised_cost)}</span>
                  <div style={note}>{t('aw_faa_total_cost', { amount: baht(a.capitalised_total) })}</div>
                </Fact>
                <Fact t={t} k="available_date" label={t('aw_faa_f_available_date')}>{a.available_date ? dmy(a.available_date) : '—'}</Fact>
                <Fact t={t} k="start_date" label={t('aw_faa_f_start_date')}>{startShown}</Fact>
                <Fact t={t} k="life" label={t('aw_faa_f_life')} hand={ov.has('rate')}>
                  {depreciable ? <>{t('aw_faa_life_years', { n: fmtNum(a.useful_life_years) })}<div style={note}>{t('aw_faa_rate_pct', { n: fmtNum(a.rate, 4) })}</div></> : t('aw_faa_not_depreciated')}
                </Fact>
                <Fact t={t} k="method" label={t('aw_faa_f_method')} hand={ov.has('method')}>{METHOD_KEY[a.method] ? t(METHOD_KEY[a.method]) : a.method}</Fact>
                {/* Not the asset's own: the company charges every asset alike (decision Q6) — shown, never set here. */}
                <Fact t={t} k="frequency" label={t('aw_faa_f_frequency')}>
                  {t(d.cadence === 'monthly' ? 'aw_fad_freq_monthly' : 'aw_fad_freq_annual')}<div style={note}>{t('aw_faa_freq_company')}</div>
                </Fact>
                <Fact t={t} k="residual" label={t('aw_faa_f_residual')}><span style={{ fontVariantNumeric: 'tabular-nums' }}>{baht(a.salvage)}</span></Fact>
                <Fact t={t} k="accumulated" label={t('aw_faa_f_accumulated')}><span style={{ fontVariantNumeric: 'tabular-nums' }}>{baht(a.accumulated)}</span></Fact>
                <Fact t={t} k="nbv" label={t('aw_faa_f_nbv')}>
                  <b style={{ fontVariantNumeric: 'tabular-nums' }}>{disposed ? '—' : baht(a.nbv)}</b>
                  {!!atDisposal && <div data-nbv-at-disposal style={note}>{atDisposal}</div>}
                </Fact>
                <Fact t={t} k="location" label={t('aw_faa_f_location')}>{a.location || '—'}</Fact>
                <Fact t={t} k="status" label={t('aw_faa_f_status')}>{removed ? removedPill : <StatusPill status={a.status} t={t} />}</Fact>
                <Fact t={t} k="documents" label={t('aw_faa_f_documents')}>{t('aw_faa_docs_n', { n: d.files.length })}</Fact>
              </div>
            </div>

            <div>
              <div style={sectionHead}>{t('aw_faa_sec_accounts')}</div>
              <div data-fa-accounts style={{ ...card, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '14px 18px' }}>
                <Fact t={t} k="acct_asset" label={t('aw_faa_acct_asset')} hand={ov.has('assetAccount')}>{d.accounts.asset ? acctLabel(lang, d.accounts.asset) : '—'}</Fact>
                <Fact t={t} k="acct_accum" label={t('aw_faa_acct_accum')} hand={ov.has('accumAccount')}>{d.accounts.accum ? acctLabel(lang, d.accounts.accum) : '—'}</Fact>
                <Fact t={t} k="acct_expense" label={t('aw_faa_acct_expense')} hand={ov.has('expenseAccount')}>{d.accounts.expense ? acctLabel(lang, d.accounts.expense) : '—'}</Fact>
                {(Number(a.opening_accum) > 0 || !!a.opening_as_of) && (
                  <Fact t={t} k="opening" label={t('aw_faa_f_opening')} wide>
                    <span data-opening>{t('aw_faa_opening_line', { amount: baht(a.opening_accum), date: a.opening_as_of ? dmy(a.opening_as_of) : '—' })}</span>
                    <div data-opening-nbv style={note}>{t('aw_faa_opening_nbv', { amount: baht(Number(a.capitalised_total) - Number(a.opening_accum)) })}</div>
                  </Fact>
                )}
              </div>
              {!!a.overrides?.length && <div style={{ ...note, marginTop: 4 }}>{t('aw_faa_hand_note')}</div>}
            </div>

            {d.source && (
              <div style={{ ...card, padding: '10px 12px', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5 }}>
                <i className="ti ti-receipt" style={{ color: 'var(--accent)' }} aria-hidden />
                <span>{t('aw_faa_source_label')}</span>
                <button type="button" data-source-link onClick={toSource} style={{ ...actBtn, color: 'var(--accent-text)' }}>
                  {t('aw_faa_source_link', { doc: srcDoc || d.source.key, n: d.source.lineNo })}
                </button>
                <span style={note}>{t('aw_faa_source_amount', { amount: baht(d.source.amount) })}</span>
              </div>
            )}

            <div>
              <div style={sectionHead}>{t('aw_faa_sec_charges')}</div>
              <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
                {!d.charges.length ? <div style={emptyLine}>{t('aw_faa_charges_none')}</div> : (
                  <table style={table} data-fa-charges>
                    <thead><tr>
                      <th style={th}>{t('aw_faa_ch_window')}</th>
                      <th style={{ ...th, textAlign: 'right' }}>{t('aw_faa_ch_days')}</th>
                      <th style={{ ...th, textAlign: 'right' }}>{t('aw_faa_ch_amount')}</th>
                      <th style={th}>{t('aw_faa_ch_entry')}</th>
                    </tr></thead>
                    <tbody>
                      {d.charges.map(c => (
                        <tr key={c.id}>
                          <td style={td}>{dmy(c.from)} – {dmy(c.to)}</td>
                          <td style={num}>{c.days}</td>
                          <td style={num}>{baht(c.amount)}</td>
                          <td style={td}><span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{c.docNo || '—'}</span> <EntryState status={c.status} t={t} /></td>
                        </tr>
                      ))}
                      <tr>
                        <td style={{ ...td, fontWeight: 800 }} colSpan={2}>{t('aw_faa_ch_total')}</td>
                        <td style={{ ...num, fontWeight: 800 }}>{baht(d.charges.reduce((s, c) => s + Number(c.amount || 0), 0))}</td>
                        <td style={td} />
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>
              {(disposed || d.disposal) && (
                <div data-fa-disposal style={{ ...card, marginTop: 8, padding: '10px 12px', fontSize: 12.5, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                  <i className="ti ti-arrow-bar-right" aria-hidden />
                  {disposed && <span>{t('aw_faa_disposed_on', { date: dmy(String(a.disposed_date)) })}</span>}
                  {disposed && a.disposal_proceeds !== null && <span>{t('aw_faa_proceeds', { amount: baht(a.disposal_proceeds) })}</span>}
                  {d.disposal && <span>{t('aw_faa_disposal_entry', { no: d.disposal.docNo })} <EntryState status={d.disposal.status} t={t} /></span>}
                </div>
              )}
            </div>

            <div>
              <div style={sectionHead}>{t('aw_faa_sec_history')}</div>
              <div data-fa-history style={{ ...card, display: 'flex', flexDirection: 'column', gap: 0, padding: '4px 15px' }}>
                {!d.movements.length && <div style={emptyLine}>{t('aw_faa_history_none')}</div>}
                {d.movements.map((m, i) => (
                  <div key={m.id} data-mv={m.kind} style={{ display: 'flex', gap: 10, padding: '9px 0', borderTop: i ? '1px solid var(--border)' : 'none', alignItems: 'flex-start' }}>
                    <i className={KIND_ICON[m.kind] || 'ti ti-point'} style={{ color: 'var(--accent)', marginTop: 2, flexShrink: 0 }} aria-hidden />
                    <div style={{ flex: 1, minWidth: 0, fontSize: 12.5 }}>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
                        <b>{KIND_KEY[m.kind] ? t(KIND_KEY[m.kind]) : t('aw_faa_mv_other')}</b>
                        {m.kind === 'edit' && (
                          <span data-mv-field={m.field}>
                            {FIELD_KEY[m.field] ? t(FIELD_KEY[m.field]) : t('aw_faa_f_other')}: <span style={{ color: 'var(--text-secondary)' }}>{fmtVal(m.field, m.from)}</span> → <b>{fmtVal(m.field, m.to)}</b>
                          </span>
                        )}
                        {m.kind === 'file_added' && <span>{m.to}</span>}
                        {m.kind === 'file_removed' && <span>{m.from}</span>}
                        {(m.kind === 'disposed' || m.kind === 'written_off') && !!m.to && <span>{t('aw_faa_on_date', { date: dmy(m.to) })}</span>}
                        {m.kind === 'registered' && d.source && <span>{t('aw_faa_mv_registered_from', { n: d.source.lineNo })}</span>}
                        {!!m.docNo && <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{m.docNo} <EntryState status={m.entryStatus} t={t} /></span>}
                      </div>
                      {!!m.note && m.kind !== 'registered' && <div style={note}>{m.note}</div>}
                    </div>
                    <div style={{ ...note, textAlign: 'right', flexShrink: 0 }}>{m.by || '—'}<div>{fmtDateTime(m.at)}</div></div>
                  </div>
                ))}
              </div>
              <div style={{ ...note, marginTop: 4 }}>
                {t('aw_faa_audit_created', { by: a.created_by_name || '—', when: fmtDateTime(a.created_at) })}
                {!!a.edited && !!a.updated_by_name && <> · {t('aw_faa_audit_updated', { by: a.updated_by_name, when: fmtDateTime(a.updated_at) })}</>}
              </div>
            </div>

            <div>
              <div style={sectionHead}>{t('aw_faa_sec_documents')}</div>
              <div data-fa-files style={{ ...card, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {!d.files.length && <div style={{ ...note, padding: '4px 0' }}>{t('aw_faa_docs_none')}</div>}
                {d.files.map(f => (
                  <div key={f.id} data-file={f.id} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5 }}>
                    <i className="ti ti-file" aria-hidden />
                    {removed ? <b>{f.filename}</b> : (
                      <button type="button" data-open-file={f.id} onClick={() => openFile(f.id)} disabled={!!busy} title={t('aw_faa_doc_open')}
                        style={{ border: 'none', background: 'none', padding: 0, color: 'var(--accent-text)', cursor: 'pointer', fontWeight: 700, fontSize: 12.5, fontFamily: 'inherit', textAlign: 'left' }}>
                        {f.filename}
                      </button>
                    )}
                    <span style={note}>{fmtSize(f.size)} · {f.by || '—'} · {fmtDateTime(f.at)}</span>
                    <div style={{ flex: 1 }} />
                    {canAct && (
                      <button type="button" data-rm-file={f.id} onClick={() => removeFile(f.id, f.filename)} disabled={!!busy} style={{ ...actBtn, color: '#B42318' }}>
                        <i className="ti ti-trash" aria-hidden /> {t('aw_faa_doc_remove')}
                      </button>
                    )}
                  </div>
                ))}
                {canAct && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <label style={{ ...actBtn, opacity: busy === 'upload' ? 0.5 : 1 }}>
                      <i className={busy === 'upload' ? 'ti ti-loader' : 'ti ti-upload'} aria-hidden /> {t('aw_faa_doc_upload')}
                      <input ref={fileRef} data-f="upload" type="file" disabled={!!busy} style={{ display: 'none' }}
                        accept={DOC_ACCEPT}
                        onChange={e => upload(e.target.files?.[0])} />
                    </label>
                    <span style={note}>{t('aw_faa_doc_types')}</span>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
        {dlg === 'dispose' && a && <DisposeDialog ctx={ctx} asset={a} onClose={() => setDlg(null)} onDone={afterPost('dispose')} />}
        {dlg === 'writeoff' && a && <WriteOffDialog ctx={ctx} asset={a} onClose={() => setDlg(null)} onDone={afterPost('writeoff')} />}
        {dlg === 'delete' && a && <DeleteDialog ctx={ctx} asset={a} onClose={() => setDlg(null)} onDone={afterDelete} />}
      </div>
    </Dialog>
  );
}
